/**
 * Helpers del Protocolo BIM por curso.
 *
 * - Cualquier usuario autenticado lee protocolos (los necesita el modal
 *   del alumno para mostrar fechas y link ACC).
 * - Admin y docente editan el de sus cursos (RLS lo enforce).
 */
import { supabase } from './supabase';
import type { CourseProtocol, CourseProtocolPatch, SoftwareVersions } from '../types/course-protocols';

/** Trae el protocolo de un curso, o null si todavía no se creó. */
export async function getCourseProtocol(courseId: string): Promise<CourseProtocol | null> {
  const { data, error } = await supabase
    .from('course_protocols')
    .select('*')
    .eq('course_id', courseId)
    .maybeSingle();
  if (error) {
    console.error('[course-protocols] getCourseProtocol:', error);
    return null;
  }
  return data as CourseProtocol | null;
}

/** Lista todos los protocolos (admin) — JOIN con courses para mostrar nombre. */
export async function listCourseProtocols(): Promise<Array<CourseProtocol & { course_name: string }>> {
  const { data, error } = await supabase
    .from('course_protocols')
    .select('*, courses!inner(name)')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[course-protocols] listCourseProtocols:', error);
    return [];
  }
  return (data ?? []).map((r: any) => ({ ...r, course_name: r.courses?.name ?? '' }));
}

/** Upsert idempotente — crea o actualiza el protocolo del curso. */
export async function upsertCourseProtocol(courseId: string, patch: CourseProtocolPatch): Promise<CourseProtocol | null> {
  const existing = await getCourseProtocol(courseId);

  if (existing) {
    const { data, error } = await supabase
      .from('course_protocols')
      .update(patch)
      .eq('id', existing.id)
      .select('*')
      .single();
    if (error) {
      console.error('[course-protocols] update:', error);
      return null;
    }
    return data as CourseProtocol;
  }

  const { data, error } = await supabase
    .from('course_protocols')
    .insert({ course_id: courseId, ...patch })
    .select('*')
    .single();
  if (error) {
    console.error('[course-protocols] insert:', error);
    return null;
  }
  return data as CourseProtocol;
}

// ────────────────────────────────────────────────────────────────────────
// Auto-seed de fechas desde los módulos del curso.
// ────────────────────────────────────────────────────────────────────────

/**
 * Asegura que el curso tenga un protocolo con las 5 fechas pobladas.
 *
 * Fuente única de verdad: las fechas viven en `courses` (start_date +
 * evaluation_1/2/3_date + final_evaluation_date). Si el protocolo NO existe,
 * lo creamos copiando esas 5 fechas. Si SÍ existe pero tiene campos null,
 * los completamos con los del curso (auto-sync incremental).
 *
 * El docente/admin puede después sobreescribir cualquier fecha del protocolo
 * sin perder esos cambios — solo completamos los nulls, no pisamos valores.
 */
export async function ensureProtocolForCourse(courseId: string): Promise<CourseProtocol | null> {
  // Traer datos del curso (incluye start_date + evaluation_X_date + final)
  const { data: course } = await supabase
    .from('courses')
    .select('name, start_date, evaluation_1_date, evaluation_2_date, evaluation_3_date, final_evaluation_date')
    .eq('id', courseId)
    .single();

  // Inferir numero_diplomado del nombre del curso (ej: "Diplomado BIM 010" → "010")
  const inferredNumero = (() => {
    if (!course?.name) return null;
    const m = String(course.name).match(/0?(\d{2,3})\b/);
    return m ? m[1].padStart(3, '0') : null;
  })();

  const courseDates = {
    fecha_inicio:        course?.start_date           ?? null,
    fecha_entrega_1:     course?.evaluation_1_date    ?? null,
    fecha_entrega_2:     course?.evaluation_2_date    ?? null,
    fecha_entrega_3:     course?.evaluation_3_date    ?? null,
    fecha_entrega_final: course?.final_evaluation_date ?? null,
  };

  const existing = await getCourseProtocol(courseId);

  if (!existing) {
    return upsertCourseProtocol(courseId, {
      numero_diplomado: inferredNumero,
      ...courseDates,
      versiones: { revit: '2025', formit: '2025', naviswork: '2025' } as SoftwareVersions,
    });
  }

  // Auto-sync: completar SOLO los campos que el protocolo tiene null.
  // Si el docente ya editó una fecha, no la pisamos.
  const patch: CourseProtocolPatch = {};
  if (!existing.fecha_inicio        && courseDates.fecha_inicio)        patch.fecha_inicio        = courseDates.fecha_inicio;
  if (!existing.fecha_entrega_1     && courseDates.fecha_entrega_1)     patch.fecha_entrega_1     = courseDates.fecha_entrega_1;
  if (!existing.fecha_entrega_2     && courseDates.fecha_entrega_2)     patch.fecha_entrega_2     = courseDates.fecha_entrega_2;
  if (!existing.fecha_entrega_3     && courseDates.fecha_entrega_3)     patch.fecha_entrega_3     = courseDates.fecha_entrega_3;
  if (!existing.fecha_entrega_final && courseDates.fecha_entrega_final) patch.fecha_entrega_final = courseDates.fecha_entrega_final;
  if (!existing.numero_diplomado    && inferredNumero)                  patch.numero_diplomado    = inferredNumero;

  if (Object.keys(patch).length === 0) return existing;
  return upsertCourseProtocol(courseId, patch);
}

// ────────────────────────────────────────────────────────────────────────
// Render del HTML con placeholders reemplazados.
// ────────────────────────────────────────────────────────────────────────

/**
 * Formatea ISO "YYYY-MM-DD" como "DD/MM/YYYY" SIN aplicar timezone.
 *
 * Bug fix importante: new Date('2026-07-01') parsea como UTC midnight.
 * En zonas horarias negativas (Chile UTC-3/4) toLocaleDateString muestra
 * el día anterior. Acá parseamos los componentes manualmente.
 *
 * Si viene un ISO completo con hora (timestamptz), tomamos solo la parte
 * YYYY-MM-DD inicial.
 */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

/**
 * Reemplaza los placeholders {{KEY}} del HTML del protocolo con los
 * valores reales del curso. Si un valor no está, muestra "—" o un
 * placeholder textual claro (el alumno verá que falta data).
 */
export function renderProtocolHtml(rawHtml: string, p: CourseProtocol | null): string {
  const values: Record<string, string> = {
    NUMERO_DIPLOMADO:  p?.numero_diplomado    ?? 'XXX',
    FECHA_INICIO:      fmtDate(p?.fecha_inicio        ?? null),
    FECHA_E1:          fmtDate(p?.fecha_entrega_1     ?? null),
    FECHA_E2:          fmtDate(p?.fecha_entrega_2     ?? null),
    FECHA_E3:          fmtDate(p?.fecha_entrega_3     ?? null),
    FECHA_FINAL:       fmtDate(p?.fecha_entrega_final ?? null),
    REVIT_VERSION:     p?.versiones?.revit     ?? '2025',
    FORMIT_VERSION:    p?.versiones?.formit    ?? '2025',
    NAVISWORK_VERSION: p?.versiones?.naviswork ?? '2025',
    LINK_ACC:          p?.link_acc ?? '#',
  };

  // Importante: la regex INCLUYE dígitos. Antes era `[A-Z_]+` y eso dejaba
  // {{FECHA_E1}}, {{FECHA_E2}}, {{FECHA_E3}} sin reemplazar — el "1"/"2"/"3"
  // no matcheaba la clase de caracteres y la regex no encontraba la captura.
  let replaced = rawHtml.replace(/\{\{([A-Z0-9_]+)\}\}/g, (_, key) => values[key] ?? '');

  // Inyectar <base href="..."> en el <head>. Sin esto las URLs relativas
  // del HTML (data/img-1.png, etc.) NO resuelven cuando servimos el
  // documento vía Blob URL — el browser intenta resolverlas contra el
  // origen "blob:..." en vez del path original.
  //
  // En campus.cubuslatam.com el Apache de BlueHosting devuelve 500 para
  // /protocolo-bim/*; en ese caso forzamos un base absoluto a vercel.app.
  if (!/<base\s/i.test(replaced)) {
    const baseHref = getProtocoloBaseHref();
    replaced = replaced.replace(/<head([^>]*)>/i, `<head$1><base href="${baseHref}">`);
  }

  return replaced;
}

function getProtocoloBaseHref(): string {
  if (typeof window === 'undefined') return '/protocolo-bim/';
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) return '/protocolo-bim/';
  if (h.endsWith('vercel.app')) return '/protocolo-bim/';
  // campus.cubuslatam.com y similares — el Apache rompe, vamos directo a Vercel
  return 'https://cubus-intranet.vercel.app/protocolo-bim/';
}
